import {
  dateKeyFromRecord,
  daysUntil,
  docStatusLabel,
  expiryInPeriod,
  parseVmDate,
  toYmdLocal,
  ymdInPeriod,
  addDaysYmd,
} from "./vmDateFilters";

const DOC_EXPIRY_FIELDS = [
  "registrationDate",
  "insuranceValidUpto",
  "fitnessValidUpto",
  "taxValidUpto",
  "pollutionValidUpto",
  "statePermitValidUpto",
  "permitUpto",
  "TemporarypermitUpto",
];

const DOC_LABELS = {
  registrationDate: "RC",
  insuranceValidUpto: "Insurance",
  fitnessValidUpto: "Fitness",
  taxValidUpto: "Road Tax",
  pollutionValidUpto: "Pollution",
  statePermitValidUpto: "State Permit",
  permitUpto: "National Permit",
  TemporarypermitUpto: "Temp Permit",
};

export function parseAmount(v) {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Prototype mdCalcPerf — total/exp/actual km/L and km diff. */
export function calcDieselPerformance(row, scheduleKmPerLitre) {
  const litres = parseAmount(row?.volume ?? row?.litres);
  const openKm = parseFloat(row?.startKm ?? row?.openingKm ?? row?.openKm) || 0;
  const closeKm = parseFloat(row?.endKm ?? row?.closingKm ?? row?.closeKm) || 0;
  const expKmL = parseFloat(scheduleKmPerLitre) || parseFloat(row?.expKmL) || 0;

  let totalKm = parseAmount(row?.totalKm ?? row?.runKm);
  if (!totalKm && openKm > 0 && closeKm > openKm) totalKm = closeKm - openKm;

  const actualKmL = litres > 0 && totalKm > 0 ? +(totalKm / litres).toFixed(1) : "";
  const expKm = litres > 0 && expKmL > 0 ? +(litres * expKmL).toFixed(1) : "";
  const actualKm = totalKm > 0 ? totalKm : "";
  const kmDiff =
    actualKm !== "" && expKm !== ""
      ? +(Number(actualKm) - Number(expKm)).toFixed(1)
      : "";

  return { totalKm: totalKm || "", actualKmL, expKm, actualKm, kmDiff, expKmL };
}

export function vehicleDocWorstStatus(vehicle) {
  let worst = "—";
  let worstDays = 9999;
  for (const field of DOC_EXPIRY_FIELDS) {
    const raw = vehicle?.[field];
    if (!raw) continue;
    const ymd =
      typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)
        ? raw.slice(0, 10)
        : toYmdLocal(parseVmDate(raw));
    if (!ymd) continue;
    const dl = daysUntil(ymd);
    const label = docStatusLabel(ymd);
    if (dl < worstDays) {
      worstDays = dl;
      worst = label;
    }
  }
  return worst;
}

export function normalizeDieselRow(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const start = raw.startKm ?? raw.openingKm ?? raw.openKm ?? "";
  const end = raw.endKm ?? raw.closingKm ?? raw.closeKm ?? "";
  let total = raw.totalKm ?? raw.runKm ?? "";
  const openN = parseFloat(start);
  const closeN = parseFloat(end);
  if ((total === "" || total == null) && Number.isFinite(openN) && Number.isFinite(closeN) && closeN >= openN) {
    total = closeN - openN;
  }
  return {
    ...raw,
    vehicleNumber: String(raw.vehicleNumber || raw.vehicle || "").trim(),
    dateKey: dateKeyFromRecord(raw, ["date", "dateYmd", "recordedAt"]),
    volume: parseAmount(raw.volume),
    amount: parseAmount(raw.totalAmount ?? raw.amount),
    totalKm: parseAmount(total),
  };
}

export function summarizeDocuments(vehicles, mode, filters) {
  let expired = 0;
  let expiringSoon = 0;
  let valid = 0;
  const upcoming = [];

  for (const v of vehicles) {
    const vn = String(v.vehicleNumber || "").trim();
    for (const field of DOC_EXPIRY_FIELDS) {
      const raw = v[field];
      if (!raw) continue;
      const ymd =
        typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)
          ? raw.slice(0, 10)
          : toYmdLocal(parseVmDate(raw));
      if (!ymd) continue;
      const status = docStatusLabel(ymd);
      if (status === "Expired") expired += 1;
      else if (status === "Expiring Soon") expiringSoon += 1;
      else if (status === "Valid") valid += 1;

      const inPeriod = expiryInPeriod(ymd, mode, filters);
      const dl = daysUntil(ymd);
      if (inPeriod && (dl <= 60 || status === "Expired")) {
        upcoming.push({
          vehicleNumber: vn,
          registerName: v.registerName || v.ownerName || "",
          doc: DOC_LABELS[field] || field,
          expiryYmd: ymd,
          daysLeft: dl,
          status,
        });
      }
    }
  }

  upcoming.sort((a, b) => a.daysLeft - b.daysLeft);
  return { expired, expiringSoon, valid, upcoming };
}

export function latestDieselByVehicle(dieselRows) {
  const map = new Map();
  for (const r of dieselRows) {
    const vn = String(r.vehicleNumber || "").trim().toUpperCase();
    if (!vn) continue;
    const key = r.dateKey || "";
    const prev = map.get(vn);
    if (!prev || key > (prev.dateKey || "")) map.set(vn, r);
  }
  return map;
}

/** Next refill due from onboarding schedule + last diesel (or schedule anchor). */
export function buildDieselScheduleUpcoming(vehicles, dieselRows, mode, filters) {
  const latest = latestDieselByVehicle(dieselRows);
  const items = [];

  for (const v of vehicles) {
    const vn = String(v.vehicleNumber || "").trim();
    if (!vn) continue;
    const interval = parseInt(v.scheduleInterval, 10);
    if (!Number.isFinite(interval) || interval < 1) continue;

    const last = latest.get(vn.toUpperCase());
    let anchor =
      last?.dateKey ||
      (v.scheduleDate
        ? typeof v.scheduleDate === "string"
          ? v.scheduleDate.slice(0, 10)
          : toYmdLocal(parseVmDate(v.scheduleDate))
        : "");
    if (!anchor) continue;

    const nextYmd = addDaysYmd(anchor, interval);
    if (!nextYmd) continue;

    const dl = daysUntil(nextYmd);
    const show =
      mode === "all" ||
      ymdInPeriod(nextYmd, mode, filters) ||
      (dl <= 0 && ymdInPeriod(anchor, mode, filters));

    if (!show && mode !== "all" && dl > 30) continue;

    items.push({
      vehicleNumber: vn,
      registerName: v.registerName || v.ownerName || "",
      intervalDays: interval,
      lastFillYmd: anchor,
      nextDueYmd: nextYmd,
      daysLeft: dl,
      status: dl < 0 ? "Overdue" : dl <= 7 ? "Due soon" : "Scheduled",
      scheduleLitres: v.scheduleLitres,
      scheduleKmPerFill: v.scheduleKmPerFill,
    });
  }

  items.sort((a, b) => a.daysLeft - b.daysLeft);
  return items;
}

export function filterRecordsByPeriod(rows, mode, filters, dateFields = ["date"]) {
  return rows.filter((r) => {
    const ymd = r.dateKey || dateKeyFromRecord(r, dateFields);
    return ymdInPeriod(ymd, mode, filters);
  });
}

export function sumDiesel(rows) {
  let amount = 0;
  let litres = 0;
  let km = 0;
  for (const r of rows) {
    amount += r.amount || 0;
    litres += r.volume || 0;
    km += r.totalKm || 0;
  }
  return { count: rows.length, amount, litres, km };
}

export function sumMaintenance(rows) {
  let amount = 0;
  for (const r of rows) {
    amount += parseAmount(r.amount ?? r.loanAmount);
  }
  return { count: rows.length, amount };
}

export function sumExpenses(rows) {
  let amount = 0;
  for (const r of rows) {
    amount += parseAmount(r.amount);
  }
  return { count: rows.length, amount };
}

export function maintenanceWithNextService(rows, mode, filters) {
  const items = [];
  for (const r of rows) {
    const next = r.nextServiceDate;
    if (!next) continue;
    const ymd =
      typeof next === "string" && /^\d{4}-\d{2}-\d{2}/.test(next)
        ? next.slice(0, 10)
        : toYmdLocal(parseVmDate(next));
    if (!ymd) continue;
    if (mode !== "all" && !ymdInPeriod(ymd, mode, filters) && daysUntil(ymd) > 30) continue;
    items.push({
      vehicleNumber: String(r.truckNo || r.vehicleNumber || "").trim(),
      maintenanceType: r.maintenanceType || "—",
      nextServiceYmd: ymd,
      daysLeft: daysUntil(ymd),
      serviceCentre: r.serviceCentre || r.workshopName || "",
    });
  }
  items.sort((a, b) => a.daysLeft - b.daysLeft);
  return items;
}

export function perVehiclePeriodRows(vehicles, diesel, maintenance, expenses) {
  const map = new Map();
  const ensure = (vn) => {
    const key = vn.toUpperCase();
    if (!map.has(key)) {
      map.set(key, {
        vehicleNumber: vn,
        dieselCount: 0,
        dieselAmount: 0,
        maintCount: 0,
        maintAmount: 0,
        expenseCount: 0,
        expenseAmount: 0,
      });
    }
    return map.get(key);
  };

  for (const v of vehicles) {
    const vn = String(v.vehicleNumber || "").trim();
    if (vn) ensure(vn);
  }
  for (const r of diesel) {
    const vn = String(r.vehicleNumber || "").trim();
    if (!vn) continue;
    const row = ensure(vn);
    row.dieselCount += 1;
    row.dieselAmount += r.amount || 0;
  }
  for (const r of maintenance) {
    const vn = String(r.truckNo || r.vehicleNumber || "").trim();
    if (!vn) continue;
    const row = ensure(vn);
    row.maintCount += 1;
    row.maintAmount += parseAmount(r.amount ?? r.loanAmount);
  }
  for (const r of expenses) {
    const vn = String(r.vehicleNumber || "").trim();
    if (!vn) continue;
    const row = ensure(vn);
    row.expenseCount += 1;
    row.expenseAmount += parseAmount(r.amount);
  }

  return [...map.values()].sort((a, b) =>
    a.vehicleNumber.localeCompare(b.vehicleNumber, undefined, { numeric: true }),
  );
}

/** Diesel rows for daily/monthly/yearly record grid (prototype daily view columns). */
export function buildDieselRecordGridRows(dieselRows, vehicleByNumber) {
  return dieselRows
    .map((r) => {
      const vn = String(r.vehicleNumber || "").trim();
      const v = vehicleByNumber.get(vn.toUpperCase()) || null;
      const schedKmL = parseFloat(v?.scheduleKmPerLitre) || 0;
      const perf = calcDieselPerformance(r, schedKmL);
      const expectKm =
        v?.scheduleKmPerFill != null && v.scheduleKmPerFill !== ""
          ? Number(v.scheduleKmPerFill)
          : schedKmL && v?.scheduleLitres
            ? Math.round(schedKmL * parseFloat(v.scheduleLitres))
            : perf.expKm;

      return {
        id: r._id || `${vn}-${r.dateKey}-${r.time}-${r.volume}`,
        vehicleNumber: vn,
        driverName: r.driverName || "—",
        ownerName: r.ownerName || v?.registerName || v?.ownerName || "—",
        paidBy: r.paidBy || "—",
        pumpName: r.pumpName || r.fuelStation || "—",
        volume: r.volume,
        amount: r.amount,
        time: r.time || "—",
        paymentMode: r.paymentMode || "—",
        schedKmL: schedKmL || "—",
        expectKm: expectKm !== "" && expectKm != null ? expectKm : perf.expKm || "—",
        startKm: r.startKm || "—",
        endKm: r.endKm || "—",
        totalKm: perf.actualKm !== "" ? perf.actualKm : "—",
        kmDiff: perf.kmDiff !== "" ? perf.kmDiff : "—",
        actualKmL: perf.actualKmL !== "" ? perf.actualKmL : "—",
        dateKey: r.dateKey,
        status: "Filled",
      };
    })
    .sort((a, b) => {
      const c = a.vehicleNumber.localeCompare(b.vehicleNumber, undefined, { numeric: true });
      if (c !== 0) return c;
      return String(a.time).localeCompare(String(b.time));
    });
}

/** Fleet vehicles with no diesel row in the current period (daily view). */
export function buildDieselNotFilledRows(vehicles, dieselRowsInPeriod) {
  const filled = new Set(
    dieselRowsInPeriod.map((r) => String(r.vehicleNumber || "").trim().toUpperCase()),
  );
  return vehicles
    .filter((v) => {
      const vn = String(v.vehicleNumber || "").trim();
      return vn && !filled.has(vn.toUpperCase());
    })
    .map((v) => ({
      vehicleNumber: v.vehicleNumber,
      ownerName: v.registerName || v.ownerName || "—",
      vehicleType: v.vehicleType || "—",
      scheduleInterval: v.scheduleInterval,
      status: "Not filled",
    }))
    .sort((a, b) =>
      String(a.vehicleNumber).localeCompare(String(b.vehicleNumber), undefined, { numeric: true }),
    );
}

export function buildMaintenanceRecordGridRows(rows) {
  return rows
    .map((r) => ({
      id: r._id || `${r.truckNo}-${r.dateKey}-${r.amount}`,
      dateKey: r.dateKey || dateKeyFromRecord(r, ["date"]),
      vehicleNumber: String(r.truckNo || r.vehicleNumber || "").trim(),
      maintenanceType: r.maintenanceType || "—",
      amount: parseAmount(r.amount ?? r.loanAmount),
      workshop: r.workshopName || r.serviceCentre || r.supplierPartyName || "—",
      driver: r.driver || r.approvedBy || "—",
      paymentMode: r.paymentMode || "—",
      nextServiceDate: r.nextServiceDate
        ? typeof r.nextServiceDate === "string"
          ? r.nextServiceDate.slice(0, 10)
          : toYmdLocal(parseVmDate(r.nextServiceDate))
        : "—",
    }))
    .sort((a, b) => {
      const c = (b.dateKey || "").localeCompare(a.dateKey || "");
      if (c !== 0) return c;
      return a.vehicleNumber.localeCompare(b.vehicleNumber, undefined, { numeric: true });
    });
}

export function enrichPerVehicleOverview(periodRows, vehicles, dieselAll, mode, periodFilters) {
  const vehicleByNumber = new Map();
  for (const v of vehicles) {
    const vn = String(v.vehicleNumber || "").trim();
    if (vn) vehicleByNumber.set(vn.toUpperCase(), v);
  }

  const upcoming = buildDieselScheduleUpcoming(vehicles, dieselAll, mode, periodFilters);
  const nextByVeh = new Map(
    upcoming.map((u) => [u.vehicleNumber.toUpperCase(), u]),
  );

  return periodRows.map((row) => {
    const v = vehicleByNumber.get(row.vehicleNumber.toUpperCase()) || {};
    const next = nextByVeh.get(row.vehicleNumber.toUpperCase());
    return {
      ...row,
      ownerName: v.registerName || v.ownerName || "—",
      vehicleType: v.vehicleType || "—",
      scheduleInterval: v.scheduleInterval ? `${v.scheduleInterval}d` : "—",
      docStatus: vehicleDocWorstStatus(v),
      nextDieselDue: next?.nextDueYmd || "—",
      nextDieselStatus: next?.status || "—",
    };
  });
}
